import { type AdminAgentNoteRead } from '@libraries/typescript/api_client';

export type NoteSourceLink =
    | {
          readonly kind: 'agent_trace';
          readonly to: '/admin/agent-traces/$conversationId';
          readonly params: { readonly conversationId: string };
      }
    | {
          readonly kind: 'agent_run';
          readonly to: '/admin/agent/$runId';
          readonly params: { readonly runId: string };
      }
    | {
          readonly kind: 'voice_call';
          readonly to: '/admin/calls/$callId';
          readonly params: { readonly callId: string };
      }
    | {
          readonly kind: 'sms_conversation';
          readonly to: '/conversations';
          readonly search: {
              readonly conversationId: string;
              readonly organizationId: string;
          };
      };

export type NoteTraceLinks = {
    // Braintrust run for the note's classification LLM call.
    readonly classificationBraintrustUrl: string | null;
    // Logfire logs for the classify worker run, tagged by note id.
    readonly classificationTraceUrl: string | null;
    // Where the note came from.
    readonly sourceLink: NoteSourceLink | null;
};

/**
 * Resolve a note's action links from the ids the API ships. `sourceLink` is
 * the note's origin: the agent trace for agent-authored notes (sidebar,
 * automation, outreach, slack) and agent-run feedback, or the voice call /
 * SMS conversation for call- and message-rating feedback.
 */
export function buildNoteLinks(note: AdminAgentNoteRead): NoteTraceLinks {
    const links = note.links;

    let sourceLink: NoteSourceLink | null = null;
    if (links.agent_conversation_id) {
        sourceLink = {
            kind: 'agent_trace',
            to: '/admin/agent-traces/$conversationId',
            params: { conversationId: links.agent_conversation_id },
        };
    } else if (links.agent_run_id) {
        sourceLink = {
            kind: 'agent_run',
            to: '/admin/agent/$runId',
            params: { runId: links.agent_run_id },
        };
    } else if (
        links.source_link_kind === 'voice_call' &&
        links.source_link_id
    ) {
        sourceLink = {
            kind: 'voice_call',
            to: '/admin/calls/$callId',
            params: { callId: links.source_link_id },
        };
    } else if (
        links.source_link_kind === 'sms_conversation' &&
        links.source_link_id
    ) {
        // Org-scoped conversation view; the deep link switches org context.
        sourceLink = {
            kind: 'sms_conversation',
            to: '/conversations',
            search: {
                conversationId: links.source_link_id,
                organizationId: note.organization_id,
            },
        };
    }

    return {
        classificationBraintrustUrl:
            links.classification_braintrust_url ?? null,
        classificationTraceUrl: links.classification_trace_url ?? null,
        sourceLink,
    };
}
